import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Volume2, Disc3 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { WaveBars } from "./Hero";
import NewsletterForm from "./NewsletterForm";
import { api, mediaUrl } from "../../lib/api";

const fmt = (s) => (!isFinite(s) ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

export default function MusicPlayer() {
  const [tracks, setTracks] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.getTracks().then(setTracks).catch(() => setTracks([]));
  }, []);

  const track = tracks?.[idx];

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume, idx]);

  useEffect(() => {
    if (track && playing) audioRef.current?.play().catch(() => setPlaying(false));
  }, [idx]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const next = () => {
    if (!tracks?.length) return;
    if (shuffle && tracks.length > 1) {
      setIdx((i) => {
        let n = i;
        while (n === i) n = Math.floor(Math.random() * tracks.length);
        return n;
      });
    } else {
      setIdx((i) => (i + 1) % tracks.length);
    }
    setProgress(0);
  };

  const prev = () => {
    if (!tracks?.length) return;
    setIdx((i) => (i - 1 + tracks.length) % tracks.length);
    setProgress(0);
  };

  const onEnded = () => {
    if (repeat) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      next();
    }
  };

  const playAt = (i) => {
    setIdx(i);
    setPlaying(true);
    setProgress(0);
  };

  return (
    <section id="music" className="relative py-24 md:py-36 bg-obsidian" data-testid="music-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Eyebrow index="05" label="MUSIC" />
          <h2 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-[0.85]" data-testid="music-heading">
            HEAR THE <span className="text-burnt">CITY.</span>
          </h2>
          <p className="mt-5 max-w-lg text-sand font-light">Music moving through the Cobain ecosystem — premieres, selects and releases, straight from the source.</p>
        </Reveal>

        {tracks === null && <div className="mt-14 h-64 border border-woodborder bg-card/30 animate-pulse" data-testid="music-skeleton" />}

        {tracks !== null && tracks.length === 0 && (
          <Reveal delay={0.12}>
            <div className="mt-14 border border-dashed border-tan/30 p-12 md:p-16 grid md:grid-cols-2 gap-10 items-center" data-testid="music-empty">
              <div>
                <Disc3 size={36} className="text-burnt" />
                <h3 className="mt-6 font-display text-4xl md:text-5xl text-cream tracking-wide">THE FIRST RELEASE IS LOADING.</h3>
                <p className="mt-4 max-w-md text-sand font-light">Cobain music drops here first — premieres, exclusives and selects. Lock in and you'll hear it before anyone else.</p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-tan mb-3">FIRST-ACCESS NOTIFICATIONS</p>
                <NewsletterForm source="music" buttonLabel="NOTIFY ME" testId="music-notify" />
              </div>
            </div>
          </Reveal>
        )}

        {tracks !== null && tracks.length > 0 && track && (
          <Reveal delay={0.12}>
            <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start" data-testid="music-player">
              <audio
                ref={audioRef}
                src={mediaUrl(track.file)}
                onTimeUpdate={(e) => setProgress(e.target.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
                onEnded={onEnded}
                data-testid="music-audio-element"
              />
              <div className="border border-woodborder bg-card/40 p-7 md:p-9">
                <div className="flex items-start gap-6">
                  {track.artwork ? (
                    <img src={mediaUrl(track.artwork)} alt={`${track.title} artwork`} className={`w-32 h-32 md:w-40 md:h-40 object-cover border border-woodborder shrink-0 ${playing ? "shadow-[0_0_40px_rgba(200,90,23,0.25)]" : ""}`} data-testid="music-artwork" />
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 border border-woodborder bg-wood flex items-center justify-center shrink-0">
                      <Disc3 size={36} className={`text-burnt ${playing ? "animate-spin-slow" : ""}`} />
                    </div>
                  )}
                  <div className="min-w-0 pt-1">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-gold-bright">NOW PLAYING</p>
                    <h3 className="mt-2 font-editorial font-extrabold text-cream text-xl md:text-2xl leading-tight truncate" data-testid="music-now-title">{track.title}</h3>
                    <p className="mt-1 font-mono text-[11px] tracking-[0.15em] text-tan" data-testid="music-now-artist">{(track.artist || "COBAIN SELECTS").toUpperCase()}</p>
                    {playing && <WaveBars className="mt-4 h-4" />}
                  </div>
                </div>

                <div className="mt-7">
                  <input
                    type="range" min={0} max={duration || 0} step={0.1} value={progress}
                    onChange={(e) => { audioRef.current.currentTime = Number(e.target.value); setProgress(Number(e.target.value)); }}
                    className="w-full accent-[#C85A17] cursor-pointer" data-testid="music-progress"
                    aria-label="Seek"
                  />
                  <div className="flex justify-between mt-1.5 font-mono text-[10px] text-clay">
                    <span data-testid="music-time-current">{fmt(progress)}</span>
                    <span data-testid="music-time-total">{fmt(duration)}</span>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setShuffle(!shuffle)} aria-label="Shuffle" data-testid="music-shuffle"
                      className={`p-2.5 rounded-full border transition-colors ${shuffle ? "border-burnt text-burnt" : "border-woodborder text-clay hover:text-tan"}`}>
                      <Shuffle size={15} />
                    </button>
                    <button onClick={prev} aria-label="Previous" data-testid="music-prev" className="p-2.5 rounded-full border border-woodborder text-sand hover:text-cream hover:border-tan/50 transition-colors">
                      <SkipBack size={17} />
                    </button>
                    <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid="music-play-toggle"
                      className="w-14 h-14 rounded-full bg-burnt hover:bg-burnt-hover text-cream flex items-center justify-center transition-all hover:shadow-[0_0_30px_rgba(200,90,23,0.5)]">
                      {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                    </button>
                    <button onClick={next} aria-label="Next" data-testid="music-next" className="p-2.5 rounded-full border border-woodborder text-sand hover:text-cream hover:border-tan/50 transition-colors">
                      <SkipForward size={17} />
                    </button>
                    <button onClick={() => setRepeat(!repeat)} aria-label="Repeat" data-testid="music-repeat"
                      className={`p-2.5 rounded-full border transition-colors ${repeat ? "border-burnt text-burnt" : "border-woodborder text-clay hover:text-tan"}`}>
                      <Repeat size={15} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Volume2 size={15} className="text-clay" />
                    <input type="range" min={0} max={1} step={0.01} value={volume} onChange={(e) => setVolume(Number(e.target.value))}
                      className="w-24 accent-[#C85A17] cursor-pointer" data-testid="music-volume" aria-label="Volume" />
                  </div>
                </div>
              </div>

              <div className="border border-woodborder bg-card/30" data-testid="music-tracklist">
                <div className="px-6 py-4 border-b border-woodborder">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-tan">THE LIBRARY — {tracks.length} {tracks.length === 1 ? "TRACK" : "TRACKS"}</span>
                </div>
                {tracks.map((t, i) => {
                  const active = i === idx;
                  return (
                    <button
                      key={t.id} onClick={() => playAt(i)}
                      data-testid={`music-track-${i}`}
                      className={`w-full flex items-center gap-4 px-6 py-4 border-b border-woodborder last:border-b-0 text-left transition-colors ${active ? "bg-burnt/10" : "hover:bg-cardhover/50"}`}
                    >
                      <span className="font-mono text-[11px] text-clay w-6">{String(i + 1).padStart(2, "0")}</span>
                      {t.artwork ? (
                        <img src={mediaUrl(t.artwork)} alt="" className="w-11 h-11 object-cover border border-woodborder" />
                      ) : (
                        <div className="w-11 h-11 border border-woodborder bg-wood flex items-center justify-center"><Disc3 size={15} className="text-clay" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`font-editorial font-bold text-sm truncate ${active ? "text-gold-bright" : "text-cream"}`}>{t.title}</p>
                        <p className="font-mono text-[10px] tracking-[0.12em] text-clay mt-0.5">{(t.artist || "COBAIN SELECTS").toUpperCase()}</p>
                      </div>
                      {active && playing && <WaveBars className="h-3.5" />}
                      {active && !playing && <Pause size={13} className="text-burnt" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
